import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Package, Clock, AlertTriangle, TrendingUp, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  PageHeader,
  MetricCard,
  DataTable,
  Column,
  TableFilters,
  KPIGrid,
} from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  poVisibilityApi,
  PoWithVisibility,
  PoSupplierPerformance,
  LeadTimeAnalysis,
  PoVisibilityDashboard,
  milestoneTypeLabels,
} from '@/api/services/po-visibility.api';
import { suppliersApi } from '@/api/services/suppliers.api';

// ==========================================
// Metric Card Component
// ==========================================

// Local MetricCard removed - using standard component from @/components/common

// ==========================================
// Main Component
// ==========================================

export default function POVisibility() {
  // State
  const [dashboard, setDashboard] = useState<PoVisibilityDashboard | null>(null);
  const [poList, setPoList] = useState<PoWithVisibility[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [supplierPerformance, setSupplierPerformance] = useState<PoSupplierPerformance[]>([]);
  const [leadTimeData, setLeadTimeData] = useState<LeadTimeAnalysis[]>([]);
  // Loading state removed - standard MetricCard doesn't use it
  const [searchQuery, setSearchQuery] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const response = await poVisibilityApi.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  // Fetch PO list
  const fetchPoList = useCallback(async () => {
    try {
      const params: Record<string, unknown> = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchQuery) params.search = searchQuery;
      if (supplierFilter !== 'all') params.supplierId = supplierFilter;

      const response = await poVisibilityApi.getAll(params);
      setPoList(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch PO list:', error);
    }
  }, [pagination.limit, pagination.page, searchQuery, supplierFilter]);

  // Fetch suppliers for filter
  const fetchSuppliers = async () => {
    try {
      const response = await suppliersApi.getAll({ limit: 100 });
      setSuppliers(
        response.data.map((s: { id: string; name: string }) => ({ id: s.id, name: s.name }))
      );
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  // Fetch supplier performance
  const fetchSupplierPerformance = async () => {
    try {
      const response = await poVisibilityApi.getPoSupplierPerformance({ limit: 20 });
      setSupplierPerformance(response.data.records);
    } catch (error) {
      console.error('Failed to fetch supplier performance:', error);
    }
  };

  // Fetch lead time analysis
  const fetchLeadTimeData = async () => {
    try {
      const response = await poVisibilityApi.getLeadTimeAnalysis({});
      setLeadTimeData(response.data);
    } catch (error) {
      console.error('Failed to fetch lead time data:', error);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchDashboard(),
        fetchSuppliers(),
        fetchSupplierPerformance(),
        fetchLeadTimeData(),
      ]);
    };
    loadData();
  }, []);

  // Fetch PO list when filters change
  useEffect(() => {
    fetchPoList();
  }, [searchQuery, supplierFilter, pagination.page, fetchPoList]);

  // Get PO status color
  const getPoStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-700';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-700';
      case 'APPROVED':
        return 'bg-emerald-100 text-emerald-700';
      case 'ORDERED':
        return 'bg-status-neutral-subtle text-status-neutral-foreground';
      case 'PARTIALLY_RECEIVED':
        return 'bg-amber-100 text-amber-700';
      case 'RECEIVED':
        return 'bg-emerald-100 text-emerald-700';
      case 'CANCELLED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const poTrackingColumns: Column<PoWithVisibility>[] = [
    {
      key: 'poNumber',
      header: 'PO Number',
      sortable: true,
      // The sheet on this page shows the milestone timeline, which the purchase
      // order's own page does not — so it stays. What was missing was any route
      // to the full record; the number now provides one.
      linkTo: (po) => `/purchase-orders/${po.id}`,
      render: (po) => <span className="font-medium">{po.poNumber}</span>,
    },
    {
      key: 'supplier',
      header: 'Supplier',
      sortable: true,
      accessorFn: (row) => row.supplier.name,
      render: (po) => <>{po.supplier.name}</>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (po) => <Badge className={getPoStatusColor(po.status)}>{po.status}</Badge>,
    },
    {
      key: 'progress',
      header: 'Progress',
      sortable: true,
      render: (po) => (
        <div className="flex items-center gap-2">
          <Progress value={po.progress} className="w-20" />
          <span className="text-sm text-muted-foreground">{po.progress}%</span>
        </div>
      ),
    },
    {
      key: 'currentMilestone',
      header: 'Current Stage',
      render: (po) =>
        po.currentMilestone ? (
          <span className="text-sm">{milestoneTypeLabels[po.currentMilestone]}</span>
        ) : (
          <>-</>
        ),
    },
    {
      key: 'expectedDate',
      header: 'Expected Date',
      sortable: true,
      render: (po) => (
        <>{po.expectedDate ? format(new Date(po.expectedDate), 'MMM d, yyyy') : '-'}</>
      ),
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      sortable: true,
      className: 'text-right',
      render: (po) => <>${po.totalAmount?.toLocaleString() ?? '0'}</>,
    },
    {
      key: 'actions',
      header: '',
      render: (po) => (
        <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} asChild>
          <Link to={`/purchase-orders/${po.id}`}>
            View timeline
            <ChevronRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      ),
    },
  ];

  const supplierPerformanceColumns: Column<PoSupplierPerformance>[] = [
    {
      key: 'supplier',
      header: 'Supplier',
      sortable: true,
      accessorFn: (row) => row.supplier.name,
      render: (perf) => <span className="font-medium">{perf.supplier.name}</span>,
    },
    {
      key: 'periodMonth',
      header: 'Period',
      sortable: true,
      render: (perf) => <>{format(new Date(perf.periodMonth), 'MMM yyyy')}</>,
    },
    {
      key: 'totalOrders',
      header: 'Total Orders',
      sortable: true,
      className: 'text-center',
    },
    {
      key: 'onTimeDeliveries',
      header: 'On-Time',
      sortable: true,
      className: 'text-center',
      render: (perf) => <span className="text-emerald-600">{perf.onTimeDeliveries}</span>,
    },
    {
      key: 'lateDeliveries',
      header: 'Late',
      sortable: true,
      className: 'text-center',
      render: (perf) => <span className="text-red-600">{perf.lateDeliveries}</span>,
    },
    {
      key: 'qualityIssues',
      header: 'Quality Issues',
      sortable: true,
      className: 'text-center',
      render: (perf) => <span className="text-amber-600">{perf.qualityIssues}</span>,
    },
    {
      key: 'onTimeRate',
      header: 'On-Time Rate',
      className: 'text-center',
      render: (perf) => {
        const onTimeRate =
          perf.onTimeDeliveries + perf.lateDeliveries > 0
            ? Math.round(
                (perf.onTimeDeliveries / (perf.onTimeDeliveries + perf.lateDeliveries)) * 100
              )
            : null;
        return onTimeRate !== null ? (
          <Badge
            className={
              onTimeRate >= 90
                ? 'bg-emerald-100 text-emerald-700'
                : onTimeRate >= 70
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-red-100 text-red-700'
            }
          >
            {onTimeRate}%
          </Badge>
        ) : (
          <>-</>
        );
      },
    },
    {
      key: 'avgLeadTimeDays',
      header: 'Avg Lead Time',
      sortable: true,
      className: 'text-center',
      render: (perf) => (
        <>{perf.avgLeadTimeDays ? `${perf.avgLeadTimeDays.toFixed(1)} days` : '-'}</>
      ),
    },
  ];

  const leadTimeColumns: Column<LeadTimeAnalysis>[] = [
    {
      key: 'supplier',
      header: 'Supplier',
      sortable: true,
      accessorFn: (row) => row.supplier.name,
      render: (lt) => <span className="font-medium">{lt.supplier.name}</span>,
    },
    {
      key: 'item',
      header: 'Item / Category',
      render: (lt) => <>{lt.item ? lt.item.name : lt.category || 'All Items'}</>,
    },
    {
      key: 'avgLeadTimeDays',
      header: 'Avg Lead Time',
      sortable: true,
      className: 'text-center',
      render: (lt) => <span className="font-medium">{lt.avgLeadTimeDays.toFixed(1)} days</span>,
    },
    {
      key: 'minLeadTimeDays',
      header: 'Min',
      sortable: true,
      className: 'text-center',
      render: (lt) => <span className="text-emerald-600">{lt.minLeadTimeDays} days</span>,
    },
    {
      key: 'maxLeadTimeDays',
      header: 'Max',
      sortable: true,
      className: 'text-center',
      render: (lt) => <span className="text-red-600">{lt.maxLeadTimeDays} days</span>,
    },
    {
      key: 'stdDeviation',
      header: 'Std Dev',
      className: 'text-center',
      render: (lt) => (
        <span className="text-muted-foreground">&plusmn;{lt.stdDeviation?.toFixed(1) ?? '0'}</span>
      ),
    },
    {
      key: 'sampleSize',
      header: 'Sample Size',
      sortable: true,
      className: 'text-center',
    },
    {
      key: 'lastCalculatedAt',
      header: 'Last Updated',
      sortable: true,
      render: (lt) => (
        <span className="text-muted-foreground">
          {format(new Date(lt.lastCalculatedAt), 'MMM d, yyyy')}
        </span>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      {/* Header */}
      <motion.div variants={fadeUp}>
        <PageHeader
          title="PO Visibility"
          description="Progress of open purchase orders by stage, with supplier delivery performance alongside."
        />
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={fadeUp}>
        <KPIGrid columns={4}>
          <MetricCard
            label="Active POs"
            value={dashboard?.kpis.totalActivePOs ?? 0}
            icon={<Package className="h-5 w-5" />}
            sentiment="info"
          />
          <MetricCard
            label="Pending Milestones"
            value={dashboard?.kpis.pendingMilestones ?? 0}
            icon={<Clock className="h-5 w-5" />}
            sentiment="warning"
          />
          <MetricCard
            label="Delayed"
            value={dashboard?.kpis.delayedMilestones ?? 0}
            icon={<AlertTriangle className="h-5 w-5" />}
            sentiment="danger"
          />
          <MetricCard
            label="On-Time Rate"
            value={dashboard?.kpis.avgOnTimeRate ? `${dashboard.kpis.avgOnTimeRate}%` : 'N/A'}
            icon={<TrendingUp className="h-5 w-5" />}
          />
        </KPIGrid>

        {/* Tabs */}
        <Tabs defaultValue="tracking" className="space-y-4">
          <TabsList>
            <TabsTrigger value="tracking">PO Tracking</TabsTrigger>
            <TabsTrigger value="performance">Supplier Performance</TabsTrigger>
            <TabsTrigger value="leadtime">Lead Time Analysis</TabsTrigger>
          </TabsList>

          {/* PO Tracking Tab */}
          <TabsContent value="tracking" className="space-y-4">
            {/* PO Table */}
            <DataTable<PoWithVisibility>
              toolbar={
                <TableFilters
                  search={searchQuery}
                  onSearchChange={setSearchQuery}
                  searchPlaceholder="Search PO number..."
                  customFilters={
                    <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Filter by supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Suppliers</SelectItem>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />
              }
              data={poList}
              columns={poTrackingColumns}
              emptyTitle="No purchase orders found"
              emptyDescription="No POs match the current filters."
              pagination
              exportable
              columnToggle
            />
          </TabsContent>

          {/* Supplier Performance Tab */}
          <TabsContent value="performance" className="space-y-4">
            <DataTable<PoSupplierPerformance>
              toolbar={
                <TableFilters
                  search={searchQuery}
                  onSearchChange={setSearchQuery}
                  searchPlaceholder="Search PO number..."
                  customFilters={
                    <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Filter by supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Suppliers</SelectItem>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />
              }
              data={supplierPerformance}
              columns={supplierPerformanceColumns}
              emptyTitle="No performance data available"
              emptyDescription="Supplier performance metrics will appear here once data is collected."
              pagination
              exportable
              columnToggle
            />
          </TabsContent>

          {/* Lead Time Analysis Tab */}
          <TabsContent value="leadtime" className="space-y-4">
            <DataTable<LeadTimeAnalysis>
              toolbar={
                <TableFilters
                  search={searchQuery}
                  onSearchChange={setSearchQuery}
                  searchPlaceholder="Search PO number..."
                  customFilters={
                    <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Filter by supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Suppliers</SelectItem>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />
              }
              data={leadTimeData}
              columns={leadTimeColumns}
              emptyTitle="No lead time data available"
              emptyDescription="Lead time analysis will appear here once data is collected."
              pagination
              exportable
              columnToggle
            />
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.main>
  );
}
